/**
 * TypeScript types for Gaushala Map visualization
 */

/**
 * Raw gaushala data as received from the API
 */
export interface RawGaushala {
  id: number;
  gaushalaName: string;
  registeredAddress: string | null;
  pincode: string | null;
  latitude: number | null;
  longitude: number | null;
  coordinateSource?: 'geocoded_from_api' | 'user_provided' | 'failed' | 'pending'; // NEW: Track coordinate source
  geocodedAddress?: string; // NEW: Full address from geocoding API
  totalCattle: number;
  milkingCows: number;
  totalLandArea: number | null;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  createdAt: string;
  headName?: string;
  headMobileNumber?: string;
  headEmail?: string;
  website?: string;
  organizationType?: string;
  yearEstablished?: number | null;
  email?: string | null;
  contactNumber?: string | null;
  headEmail?: string | null;
  totalCows?: number | null;
  totalBulls?: number | null;
  totalCalves?: number | null;
  avgMilkProduction?: number | null;
  shelterCapacity?: number | null;
  waterSource?: string | null;
  electricityAvailable?: number | null;
  annualBudget?: number | null;
  sourceOfFunds?: string | null;
  bankAccountDetails?: string | null;
  organizationPan?: string | null;
  registrationNumber?: string;
  updatedAt?: string;
  [key: string]: any; // For additional fields
}

/**
 * Geocoding result from Nominatim API
 */
export interface GeocodingResult {
  latitude: number;
  longitude: number;
  confidence: 'high' | 'medium' | 'low';
  source: string;
  address?: string;
}

/**
 * Data source indicator
 */
export type DataSource =
  | 'database'
  | 'geocoded_from_pincode'
  | 'geocoded_from_address'
  | 'geocoded_from_name'
  | 'fallback';

/**
 * Normalized gaushala object with guaranteed lat/long
 * This is what gets displayed on the map
 */
export interface NormalizedGaushala {
  id: number;
  name: string;
  address: string;
  pincode: string | null;
  latitude: number;
  longitude: number;
  coordinateSource?: 'geocoded_from_api' | 'user_provided' | 'failed' | 'pending'; // NEW: Track source
  geocodedAddress?: string; // NEW: Full address from API
  dataSource: DataSource;
  confidence: 'high' | 'medium' | 'low';
  metadata: GaushalaMetadata;
}

/**
 * Gaushala metadata for display in popups
 */
export interface GaushalaMetadata {
  totalCattle: number;
  milkingCows: number;
  totalLandArea: number | null;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  createdAt: string;
  headName?: string;
  headMobileNumber?: string;
  headEmail?: string;
  website?: string;
}

/**
 * State-wise statistics
 */
export interface StateStatistics {
  [stateName: string]: number;
}

/**
 * Gaushala map statistics
 */
export interface GaushalaMapStatistics {
  total: number;
  fromDatabase: number;
  geocoded: number;
  byState: StateStatistics;
}

/**
 * Map state for hook
 */
export interface UseGaushalaMapState {
  gaushalas: NormalizedGaushala[];
  isLoading: boolean;
  isGeocoding: boolean;
  progress: { current: number; total: number } | null;
  error: string | null;
  statistics: GaushalaMapStatistics;
}

/**
 * Geocoding progress
 */
export interface GeocodingProgress {
  current: number;
  total: number;
  percentage: number;
  estimatedTimeRemaining?: number;
}

/**
 * Map legend item
 */
export interface MapLegendItem {
  key: string;
  label: string;
  color: string;
  icon: string;
  description: string;
}

/**
 * Marker cluster info
 */
export interface MarkerCluster {
  latitude: number;
  longitude: number;
  count: number;
  gaushalas: NormalizedGaushala[];
}

/**
 * Geocoding options
 */
export interface GeocodingOptions {
  onProgress?: (progress: GeocodingProgress) => void;
  retryAttempts?: number;
  delayBetweenRequests?: number; // in milliseconds
  timeout?: number; // in milliseconds
  service?: 'nominatim' | 'google' | 'opencage';
}

/**
 * Map container props
 */
export interface MapContainerProps {
  gaushalas: NormalizedGaushala[];
  onMarkerClick?: (gaushala: NormalizedGaushala) => void;
  selectedGaushala?: NormalizedGaushala | null;
  isLoading?: boolean;
  center?: [number, number];
  zoom?: number;
}

/**
 * Marker popup props
 */
export interface MarkerPopupProps {
  gaushala: NormalizedGaushala;
  onClose: () => void;
}

/**
 * Statistics sidebar props
 */
export interface StatisticsSidebarProps {
  statistics: GaushalaMapStatistics;
  onStateClick?: (state: string) => void;
}

/**
 * Data source indicator props
 */
export interface DataSourceIndicatorProps {
  dataSource: DataSource;
  confidence: 'high' | 'medium' | 'low';
  showLabel?: boolean;
}

/**
 * Coordinate validation result
 */
export interface CoordinateValidation {
  isValid: boolean;
  errors: string[];
}

/**
 * India boundaries
 */
export const INDIA_BOUNDS = {
  minLatitude: 8.0,
  maxLatitude: 35.0,
  minLongitude: 68.0,
  maxLongitude: 97.0,
};

/**
 * India center coordinates
 */
export const INDIA_CENTER = {
  latitude: 20.5937,
  longitude: 78.9629,
};

/**
 * Default map zoom levels
 */
export const MAP_ZOOM_LEVELS = {
  WORLD: 2,
  COUNTRY: 4,
  STATE: 8,
  CITY: 12,
  STREET: 15,
  BUILDING: 18,
};

/**
 * Data source colors for UI
 */
export const DATA_SOURCE_COLORS = {
  database: '#22c55e', // Green
  geocoded_from_pincode: '#f59e0b', // Amber
  geocoded_from_address: '#f97316', // Orange
  geocoded_from_name: '#ec4899', // Pink
  fallback: '#6b7280', // Gray
};

/**
 * Confidence level display
 */
export const CONFIDENCE_LEVELS = {
  high: { label: 'Verified', emoji: '✓', color: '#22c55e' },
  medium: { label: 'Probable', emoji: '⚠️', color: '#f59e0b' },
  low: { label: 'Approximate', emoji: '❓', color: '#ef4444' },
};

// ============================================
// MANUFACTURING UNIT TYPES
// ============================================

/**
 * Raw manufacturing unit data from API
 */
export interface RawManufacturingUnit {
  id: number;
  name: string;
  unitCode?: string;
  location?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  coordinateSource?: 'geocoded_from_api' | 'user_provided' | 'failed' | 'pending'; // NEW: Track source
  geocodedAddress?: string; // NEW: Full address from API
  capacity?: number;
  status?: string;
  createdAt?: string;
  [key: string]: any;
}

/**
 * Normalized manufacturing unit for map display
 */
export interface NormalizedManufacturingUnit {
  id: number;
  name: string;
  unitCode: string;
  address: string;
  latitude: number;
  longitude: number;
  coordinateSource?: 'geocoded_from_api' | 'user_provided' | 'failed' | 'pending'; // NEW: Track source
  geocodedAddress?: string; // NEW: Full address from API
  dataSource: DataSource;
  confidence: 'high' | 'medium' | 'low';
  metadata: ManufacturingUnitMetadata;
}

/**
 * Manufacturing unit metadata for popups
 */
export interface ManufacturingUnitMetadata {
  capacity?: number;
  status?: string;
  createdAt?: string;
  location?: string;
}

/**
 * Manufacturing map statistics
 */
export interface ManufacturingMapStatistics {
  total: number;
  fromDatabase: number;
  geocoded: number;
}

/**
 * Combined map statistics for legend
 */
export interface CombinedMapStatistics {
  gaushalas: number;
  manufacturingUnits: number;
}
